import { EventDataTypes } from '../events/types'
import { MetricTypeConfigMap } from '../metrics/types'
import { OutcomeTypeConfigMap } from '../outcomes/types'
import config from './config.json'

interface FeatureConfig<V> {
  key: string
  value: V
  name: string
  description: string
  options: { name: string; value: V }[]
  inherited?: boolean
}

type FeatureConfigMap<V> = {
  [key: string]: FeatureConfig<V>
}

export interface TagConfig {
  name: string
  value: string
  color: string
  description: string
  enabled: boolean
}

export type TagConfigMap = {
  [key: string]: TagConfig
}

export type EventTypeBase<v = EventDataTypes> = {
  name: { en: string }
  value: v
  abbr: string
  isSettable?: boolean
  metrics: MetricTypeConfigMap
  outcomes: OutcomeTypeConfigMap
  features: {
    [featureKey: string]: FeatureConfig<any>['value']
  }
  tags: TagConfigMap
}

export type EventTypeConfig = EventTypeBase & {
  subTypes: {
    [subType: string]: EventTypeBase
  }
}

export type DataTypeEventConfig = EventTypeBase<EventDataTypes> & {
  types: {
    [type: string]: EventTypeConfig
  }
}

export type HierarchyLevelEventConfig = EventTypeBase & {
  dataTypes: {
    [dataType: string]: DataTypeEventConfig
  }
}

export type HierarchyLevelsEventConfig = {
  [hierarchyLevel: string]: HierarchyLevelEventConfig
}

export type SessionSubTypeConfig = EventTypeBase & {
  // Optional: a subType that omits this inherits its parent sessionType's
  // normalized hierarchyLevels tree (see normalizeConfig).
  hierarchyLevels?: HierarchyLevelsEventConfig
}

export type SessionTypeConfig = EventTypeBase & {
  // Optional: shared hierarchyLevels tree authored once here and cascaded down
  // to sessionSubTypes that omit their own (see normalizeConfig).
  hierarchyLevels?: HierarchyLevelsEventConfig
  sessionSubTypes: {
    [sessionSubType: string]: SessionSubTypeConfig
  }
}

export type SessionEventTypesConfig = EventTypeBase & {
  sessionTypes: {
    [sessionType: string]: SessionTypeConfig
  }
}

export type SportConfig = {
  name: { en: string }
  abbr: string
  features: FeatureConfigMap<any>
  playerSummaryMetrics: MetricTypeConfigMap
  events: {
    Session: SessionEventTypesConfig
  }
}

export type SportsConfig = {
  shortcuts: {
    [key: string]: {
      name: string
      description: string
      shortcut: string
    }
  }
  playerSummaryMetrics: MetricTypeConfigMap
  eventMetrics: {
    [eventTypeKey: string]: MetricTypeConfigMap
  }
  eventOutcomes: {
    [eventTypeKey: string]: OutcomeTypeConfigMap
  }
  eventTags: {
    [eventTypeKey: string]: TagConfigMap
  }
  eventFeatures: {
    [eventTypeKey: string]: FeatureConfigMap<any>
  }
  defaultFeatures: FeatureConfigMap<any>
  sportTypes: {
    [sportType: string]: SportConfig
  }
}

export function normalizeConfig(config: SportsConfig): SportsConfig {
  const {
    playerSummaryMetrics: defaultPlayerSummaryMetrics,
    eventMetrics: defaultEventMetrics,
    eventOutcomes: defaultEventOutcomes,
    eventFeatures,
    eventTags: defaultEventTags,
    defaultFeatures
  } = config

  const overrideDefaults = (
    obj: FeatureConfigMap<any> | OutcomeTypeConfigMap | TagConfigMap,
    parentObj: FeatureConfigMap<any> | OutcomeTypeConfigMap | TagConfigMap
  ): void => {
    for (const key in parentObj) {
      obj[key] = {
        ...parentObj[key],
        ...obj[key]
      }
    }
  }

  const overrideDefaultMetrics = (
    obj: MetricTypeConfigMap,
    parentObj: MetricTypeConfigMap
  ): void => {
    for (const key in parentObj) {
      // NOTE: Possibly a bit hacky, but what we are doing here is removing any keys where enable
      // was explicitly set to false (which takes advantage of default value being undefined).
      // This will remove these keys from any lists that are created without checking the enable
      // property (e.g. column headers and metric filters). Possibly better to filter each list?
      if (obj[key] && obj[key].enable === false) {
        delete obj[key]
        continue
      }

      obj[key] = {
        ...parentObj[key],
        ...obj[key]
      }
    }
  }

  // Normalizes a hierarchyLevels tree (hierarchyLevel -> dataType -> type ->
  // subType) in place against the global defaults. Extracted so it can run
  // against either a sessionType-level tree (shared, authored once) or a
  // sessionSubType-level tree (the subType's own override).
  const normalizeHierarchyLevels = (
    hierarchyLevels: HierarchyLevelsEventConfig
  ): void => {
    for (const hierarchyLevelKey in hierarchyLevels) {
      const hierarchyLevel = hierarchyLevels[hierarchyLevelKey]
      overrideDefaultMetrics(
        hierarchyLevel.metrics,
        defaultEventMetrics[hierarchyLevelKey]
      )
      overrideDefaults(
        hierarchyLevel.outcomes,
        defaultEventOutcomes[hierarchyLevelKey]
      )
      hierarchyLevel.features = {
        ...eventFeatures[hierarchyLevelKey],
        ...hierarchyLevel.features
      }
      overrideDefaults(hierarchyLevel.tags, defaultEventTags[hierarchyLevelKey])

      if (hierarchyLevel.dataTypes) {
        for (const dataTypeKey in hierarchyLevel.dataTypes) {
          const dataType = hierarchyLevel.dataTypes[dataTypeKey]
          overrideDefaultMetrics(
            dataType.metrics,
            defaultEventMetrics[dataTypeKey]
          )
          overrideDefaults(dataType.outcomes, defaultEventOutcomes[dataTypeKey])
          dataType.features = {
            ...eventFeatures[dataTypeKey],
            ...dataType.features
          }
          overrideDefaults(dataType.tags, defaultEventTags[dataTypeKey])

          if (dataType.types) {
            for (const typeKey in dataType.types) {
              const type = dataType.types[typeKey]
              overrideDefaultMetrics(type.metrics, dataType.metrics)
              overrideDefaults(type.outcomes, dataType.outcomes)
              type.features = {
                ...dataType.features,
                ...type.features
              }
              overrideDefaults(type.tags, dataType.tags)

              if (type.subTypes) {
                for (const subTypeKey in type.subTypes) {
                  const subType = type.subTypes[subTypeKey]
                  overrideDefaultMetrics(subType.metrics, type.metrics)
                  overrideDefaults(subType.outcomes, type.outcomes)
                  subType.features = {
                    ...type.features,
                    ...subType.features
                  }
                  overrideDefaults(subType.tags, type.tags)
                }
              }
            }
          }
        }
      }
    }
  }

  for (const key in config.sportTypes) {
    const sportType = config.sportTypes[key]
    overrideDefaults(sportType.features, defaultFeatures)

    if (sportType.playerSummaryMetrics) {
      overrideDefaultMetrics(
        sportType.playerSummaryMetrics,
        defaultPlayerSummaryMetrics
      )
    }

    if (sportType.events && sportType.events.Session) {
      overrideDefaultMetrics(
        sportType.events.Session.metrics,
        defaultEventMetrics.Session
      )
      overrideDefaults(
        sportType.events.Session.outcomes,
        defaultEventOutcomes.Session
      )
      sportType.events.Session.features = {
        ...eventFeatures.Session,
        ...sportType.events.Session.features
      }
      overrideDefaults(sportType.events.Session.tags, defaultEventTags.Session)
    }

    if (sportType.events && sportType.events.Session.sessionTypes) {
      for (const sessionTypeKey in sportType.events.Session.sessionTypes) {
        const sessionType =
          sportType.events.Session.sessionTypes[sessionTypeKey]
        overrideDefaultMetrics(sessionType.metrics, defaultEventMetrics.Session)
        overrideDefaults(sessionType.outcomes, defaultEventOutcomes.Session)
        sessionType.features = {
          ...eventFeatures.Session,
          ...sessionType.features
        }
        overrideDefaults(sessionType.tags, defaultEventTags.Session)

        // Normalize the shared sessionType-level tree once (if authored here),
        // so subTypes that omit their own can inherit the already-normalized
        // result.
        if (sessionType.hierarchyLevels) {
          normalizeHierarchyLevels(sessionType.hierarchyLevels)
        }

        if (sessionType.sessionSubTypes) {
          for (const sessionSubTypeKey in sessionType.sessionSubTypes) {
            const sessionSubType =
              sessionType.sessionSubTypes[sessionSubTypeKey]

            overrideDefaultMetrics(sessionSubType.metrics, sessionType.metrics)
            overrideDefaults(sessionSubType.outcomes, sessionType.outcomes)
            sessionSubType.features = {
              ...sessionType.features,
              ...sessionSubType.features
            }
            overrideDefaults(sessionSubType.tags, sessionType.tags)

            if (sessionSubType.hierarchyLevels) {
              // The subType declares its own tree (an override) — normalize it
              // as before.
              normalizeHierarchyLevels(sessionSubType.hierarchyLevels)
            } else if (sessionType.hierarchyLevels) {
              // The subType omits its tree — inherit a deep clone of the
              // already-normalized sessionType tree. Cloning keeps each subType's
              // tree independent (no shared object references between siblings).
              sessionSubType.hierarchyLevels = structuredClone(
                sessionType.hierarchyLevels
              )
            }
          }
        }

        // The sessionType-level tree is a source-authoring dedup device only.
        // Once distributed to the subTypes that omit their own, remove it so the
        // normalized sessionType node keeps its original shape (no hierarchyLevels
        // key) — the normalized output is identical to pre-dedup.
        delete sessionType.hierarchyLevels
      }
    }
  }

  return config
}

// The set of valid player-summary metric keys, derived directly from config.json so
// that consumers (e.g. the drill breakdown accessor maps) can be type-checked to line
// up with the metrics actually defined here — a typo or an accessor for a non-existent
// metric becomes a compile error.
export type PlayerSummaryMetricKey =
  keyof (typeof config)['playerSummaryMetrics']

export const sportsConfig = normalizeConfig(config as any)
